import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import MetaSDKLoader from "../../components/Containers/MetaSDKLoader";
import LinearLoaderWithTime from "../../components/Loaders/LinearLoaderWithTime";
import Button from "../../components/Buttons/Button";
import {
  getMicaConnectDriver,
  MICA_CONNECT_SUCCESS_MESSAGE,
  resolveMicaConnectPlatform,
  MICA_CONNECT_PLATFORM_META,
} from "./drivers/registry";
import { buildMicaRedirectPath } from "../../utils/micaRedirectUrls";
import { META } from "../../utils/constants";
import "./drivers/metaDriver";

const PHASE_VALIDATING = "validating";
const PHASE_READY = "ready";
const PHASE_CONNECTING = "connecting";
const PHASE_ERROR = "error";

const VALIDATE_LOADER_TIME_MS = 10 * 1000;
const CONNECT_LOADER_TIME_MS = 45 * 1000;
const CONNECT_LABELS = [
  "Abriendo autorización de Meta",
  "Guardando la conexión",
];
const PAGE_TITLE = "¡Todo listo para conectar tu cuenta!";

/**
 * Public MICA connect page.
 * Canonical route: /mica/connect/meta?token=...
 * Alias /mica/connect/instagram resolves to Meta driver (no duplicated OAuth).
 * Validates token on load; FB.login only starts after an explicit tap (iOS-safe).
 */
const MicaConnectPage = () => {
  const { platform: platformParam } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const theme = useTheme();
  const progressBarColor =
    theme.palette?.button?.primary?.main || theme.palette.primary.main;
  const loaderSx = {
    width: "100%",
    "& .MuiLinearProgress-root": {
      backgroundColor: "rgba(0, 0, 0, 0.12)",
      "& .MuiLinearProgress-bar": {
        backgroundColor: progressBarColor,
      },
    },
  };

  const [phase, setPhase] = useState(PHASE_VALIDATING);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [tokenReady, setTokenReady] = useState(false);
  const generationRef = useRef(0);

  const canonicalPlatform = useMemo(
    () => resolveMicaConnectPlatform(platformParam),
    [platformParam]
  );

  const driver = useMemo(
    () => getMicaConnectDriver(canonicalPlatform),
    [canonicalPlatform]
  );

  const runValidate = useCallback(async () => {
    if (!token || !driver) return;

    const generation = ++generationRef.current;
    setPhase(PHASE_VALIDATING);
    setError(null);
    setTokenReady(false);

    try {
      const result = await driver.validate({ token });
      if (generation !== generationRef.current) return;

      if (!result?.ok) {
        setError(result?.message || "Token inválido o expirado");
        setPhase(PHASE_ERROR);
        return;
      }

      setTokenReady(true);
      setPhase(PHASE_READY);
    } catch (err) {
      if (generation !== generationRef.current) return;
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Error al validar el enlace"
      );
      setPhase(PHASE_ERROR);
    }
  }, [token, driver]);

  useEffect(() => {
    if (!token || !driver) return undefined;

    runValidate();
    return () => {
      generationRef.current += 1;
    };
  }, [token, driver, retryKey, runValidate]);

  const onConnectClick = async () => {
    if (!token || !driver || phase === PHASE_CONNECTING) return;

    const generation = ++generationRef.current;
    setPhase(PHASE_CONNECTING);
    setError(null);

    try {
      const result = await driver.connect({
        token,
        onStatus: () => {},
      });
      if (generation !== generationRef.current) return;

      if (!result?.ok) {
        setError(result?.message || "No se pudo completar la conexión");
        setPhase(PHASE_ERROR);
        return;
      }
      navigate(buildMicaRedirectPath(MICA_CONNECT_SUCCESS_MESSAGE), {
        replace: true,
      });
    } catch (err) {
      if (generation !== generationRef.current) return;
      setError(
        err?.response?.data?.message || err?.message || "Error al conectar Meta"
      );
      setPhase(PHASE_ERROR);
    }
  };

  const onRetry = () => {
    generationRef.current += 1;
    setError(null);

    if (tokenReady) {
      // Token already validated — show connect button again (fresh user gesture).
      setPhase(PHASE_READY);
      return;
    }

    setRetryKey((key) => key + 1);
  };

  if (platformParam === "instagram") {
    const qs = token ? `?token=${encodeURIComponent(token)}` : "";
    return (
      <Navigate
        to={`/mica/connect/${MICA_CONNECT_PLATFORM_META}${qs}`}
        replace
      />
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 3,
        backgroundColor: "#fafafa",
      }}
    >
      <MetaSDKLoader />
      <Box
        sx={{
          width: "100%",
          maxWidth: 520,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        {!driver && (
          <>
            <Typography variant="h6" align="center">
              Conexión no disponible
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              La plataforma &quot;{platformParam}&quot; no tiene driver
              registrado.
            </Typography>
          </>
        )}

        {driver && !token && (
          <Typography variant="body1" color="error" align="center">
            Falta el token de conexión. Abre el enlace completo que te envió
            Mica.
          </Typography>
        )}

        {driver && token && (
          <>
            {phase === PHASE_VALIDATING && (
              <LinearLoaderWithTime
                key={`validate-${retryKey}`}
                labelSide="bottom"
                time={VALIDATE_LOADER_TIME_MS}
                label="Cargando Configuración..."
                sx={loaderSx}
                sxLabel={{
                  mt: 1.5,
                  fontSize: 15,
                }}
              />
            )}

            {(phase === PHASE_READY ||
              (phase === PHASE_ERROR && tokenReady)) && (
              <Typography
                variant="h5"
                align="center"
                sx={{ fontWeight: 600, mb: 1 }}
              >
                {PAGE_TITLE}
              </Typography>
            )}

            {phase === PHASE_READY && (
              <Button
                variant="contained"
                color="primary"
                onClick={onConnectClick}
              >
                Conectar Cuenta
              </Button>
            )}

            {phase === PHASE_ERROR && (
              <>
                <Typography variant="body1" color="error" align="center">
                  {error}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={tokenReady ? onConnectClick : onRetry}
                >
                  {tokenReady ? "Conectar Cuenta" : "Reintentar"}
                </Button>
              </>
            )}

            {phase === PHASE_CONNECTING && (
              <LinearLoaderWithTime
                key={`connect-${retryKey}`}
                labelSide="bottom"
                platform={META}
                time={CONNECT_LOADER_TIME_MS}
                labels={CONNECT_LABELS}
                sx={loaderSx}
                sxLabel={{
                  mt: 1.5,
                  fontSize: 15,
                }}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default MicaConnectPage;
